import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useIsMobile from 'hooks/useIsMobile';
import type { ComponentType, FC, ReactNode, SVGProps } from 'react';
import { NAVY, RADIUS_COIN, SHADOW, SURFACE_CONTAINER_LOW, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';

export interface PillTab<V extends string | number> {
  value: V;
  label: string;
  count?: number;
  Icon?: ComponentType<SVGProps<SVGSVGElement>> | ComponentType<{ sx?: object }>;
}

interface PillTabsProps<V extends string | number> {
  tabs: ReadonlyArray<PillTab<V>>;
  value: V;
  onChange: (value: V) => void;
  ariaLabel?: string;
  /** Let the track run onto a second row instead of overflowing when the tabs outgrow it. */
  wrap?: boolean;
}

const PillTabs = <V extends string | number>({
  tabs,
  value,
  onChange,
  ariaLabel,
  wrap = false,
}: PillTabsProps<V>): ReactNode => {
  const { isMobile } = useIsMobile();

  return (
    <Box
      role="tablist"
      aria-label={ariaLabel}
      sx={{
        display: 'inline-flex',
        gap: 0.5,
        bgcolor: SURFACE_CONTAINER_LOW,
        borderRadius: RADIUS_COIN,
        p: 0.5,
        flexShrink: 0,
        ...(wrap ? { flexWrap: 'wrap' } : {}),
      }}
    >
      {tabs.map((tab) => {
        const isActive = value === tab.value;
        const hasCount = typeof tab.count === 'number';
        const IconCmp = tab.Icon as FC<{ sx?: object }> | undefined;
        return (
          <Tooltip key={String(tab.value)} title={isMobile && IconCmp ? tab.label : ''} placement="bottom">
            <Box
              role="tab"
              aria-selected={isActive}
              onClick={() => onChange(tab.value)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.75,
                px: { xs: 1, md: 2 },
                py: 0.75,
                borderRadius: RADIUS_COIN,
                cursor: 'pointer',
                bgcolor: isActive ? SURFACE_CONTAINER_LOWEST : 'transparent',
                boxShadow: isActive ? SHADOW : 'none',
                transition: 'all 0.2s ease',
                '&:hover': { bgcolor: isActive ? SURFACE_CONTAINER_LOWEST : `${NAVY}06` },
              }}
            >
              {isMobile && IconCmp ? (
                <IconCmp sx={{ fontSize: '1.1rem', color: isActive ? NAVY : `${NAVY}80` }} />
              ) : (
                <Typography
                  sx={{
                    fontFamily: 'Jost, sans-serif',
                    fontWeight: isActive ? 700 : 500,
                    fontSize: '0.8rem',
                    color: NAVY,
                    whiteSpace: 'nowrap',
                    userSelect: 'none',
                  }}
                >
                  {tab.label}
                </Typography>
              )}
              {hasCount && (
                <Box
                  component="span"
                  sx={{
                    fontFamily: 'Jost, sans-serif',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    color: isActive ? SURFACE_CONTAINER_LOWEST : `${NAVY}60`,
                    bgcolor: isActive ? NAVY : `${NAVY}12`,
                    borderRadius: '10px',
                    minWidth: 20,
                    height: 18,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 0.5,
                    lineHeight: 1,
                  }}
                >
                  {tab.count}
                </Box>
              )}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};

export default PillTabs;
